<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">部门管理</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ list.length }}</b> 个部门</span>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="部门名称">
            <el-input v-model="query.keyword" placeholder="请输入部门名称" clearable style="width: 200px" />
          </el-form-item>
          <el-form-item label="所属公司">
            <el-select v-model="query.company_id" placeholder="全部" clearable style="width: 180px">
              <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id" />
            </el-select>
          </el-form-item>
          <el-form-item label="状态">
            <el-select v-model="query.status" placeholder="全部" clearable style="width: 110px">
              <el-option label="启用" :value="1" />
              <el-option label="停用" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">新增部门</el-button>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table
        v-loading="loading"
        :data="tableTree"
        row-key="id"
        border
        size="small"
        :tree-props="{ children: 'children' }"
        default-expand-all
        height="100%"
      >
        <el-table-column prop="name" label="部门名称" min-width="220"/>
        <el-table-column prop="company_name" label="所属公司" min-width="200"/>
        <el-table-column label="负责人" width="110">
          <template slot-scope="scope">{{ scope.row.leader_name || '—' }}</template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="80" align="center"/>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'" size="mini" effect="plain">
              {{ scope.row.status === 1 ? '启用' : '停用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleAddChild(scope.row)">加子部门</el-button>
            <el-button type="text" size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="mini" class="text-danger-btn" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog :title="form.id ? '编辑部门' : '新增部门'" :visible.sync="dialogVisible" width="680px" append-to-body>
      <el-form ref="form" :model="form" :rules="rules" label-width="110px">
        <el-form-item label="所属公司" prop="company_id">
          <el-select v-model="form.company_id" placeholder="请选择" style="width: 100%" @change="form.parent_id = undefined">
            <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="上级部门">
          <el-select v-model="form.parent_id" placeholder="顶级部门" clearable style="width: 100%">
            <el-option v-for="d in deptOptions" :key="d.id" :label="d.name" :value="d.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="部门名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入部门名称" maxlength="64" />
        </el-form-item>
        <el-form-item label="负责人">
          <el-select v-model="form.leader_id" placeholder="未设置" clearable style="width: 100%">
            <el-option v-for="p in persons" :key="p.id" :label="p.name" :value="p.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sort" :min="0" :max="999" controls-position="right" />
        </el-form-item>
        <el-form-item label="状态">
          <el-switch v-model="form.status" :active-value="1" :inactive-value="0" active-text="启用" inactive-text="停用" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" @click="submit">确 定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getDeptList,
  createDept,
  updateDept,
  deleteDept,
  getCompanyOptions,
  getPersonList
} from '@/api/system'

// 下拉类字段默认留空（undefined 显示 placeholder），提交时再归一化（parent_id/leader_id 顶级/未设置=0）
const emptyForm = () => ({
  id: 0, parent_id: undefined, company_id: undefined, name: '', leader_id: undefined, sort: 1, status: 1
})

export default {
  name: 'DeptManage',
  data() {
    return {
      loading: false,
      list: [],
      query: { keyword: '', company_id: '', status: '' },
      dialogVisible: false,
      form: emptyForm(),
      companies: [], // 所属公司下拉（走公司管理接口）
      persons: [], // 负责人下拉（走人员列表接口）
      rules: {
        name: [{ required: true, message: '请输入部门名称', trigger: 'blur' }],
        company_id: [{ required: true, message: '请选择所属公司', trigger: 'change' }]
      }
    }
  },
  computed: {
    // 上级部门可选范围：同公司；编辑时再排除自己与自己的子孙（后端还会再防环）。
    // 注意：新增（含「加子部门」form.id=0）不能走 collectDescendants(0)——
    // 那会把全部顶级部门（parent_id=0）误当「0 的子孙」排除，预填的上级就匹配不到选项、裸显 id
    deptOptions() {
      if (!this.form.id) {
        return this.list.filter(i => i.company_id === this.form.company_id)
      }
      const exclude = [this.form.id].concat(this.collectDescendants(this.form.id))
      return this.list.filter(i =>
        i.company_id === this.form.company_id &&
        exclude.indexOf(i.id) === -1
      )
    },
    tableTree() {
      return this.buildTree(this.list)
    }
  },
  created() {
    this.loadData()
    this.loadCompanies()
  },
  methods: {
    // 全量列表（无分页，前端建树），筛选条件走服务端过滤
    loadData() {
      this.loading = true
      const params = {}
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.company_id !== '' && this.query.company_id !== null) params.company_id = this.query.company_id
      if (this.query.status !== '' && this.query.status !== null) params.status = this.query.status

      getDeptList(params).then(res => {
        this.list = (res.data && res.data.list) || []
      }).finally(() => {
        this.loading = false
      })
    },
    loadCompanies() {
      getCompanyOptions().then(res => {
        this.companies = (res.data || [])
      })
    },
    loadPersons() {
      // 负责人下拉：拉第一页大页容量（人员量小够用）
      getPersonList({ page: 1, page_size: 100 }).then(res => {
        this.persons = (res.data && res.data.list) || []
      })
    },
    buildTree(list) {
      const map = {}
      const roots = []
      list.forEach(item => {
        map[item.id] = Object.assign({}, item, { children: [] })
      })
      list.forEach(item => {
        const node = map[item.id]
        if (item.parent_id && map[item.parent_id] && item.parent_id !== item.id) {
          map[item.parent_id].children.push(node)
        } else {
          roots.push(node)
        }
      })
      return roots
    },
    // 收集某节点的全部子孙 id（编辑时避免把上级指向自己的后代）
    collectDescendants(id) {
      const result = []
      const walk = pid => {
        this.list.forEach(item => {
          if (item.parent_id === pid) {
            result.push(item.id)
            walk(item.id)
          }
        })
      }
      walk(id)
      return result
    },
    handleSearch() {
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', company_id: '', status: '' }
      this.loadData()
    },
    handleAdd() {
      this.loadPersons()
      this.form = emptyForm()
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleAddChild(row) {
      this.loadPersons()
      this.form = Object.assign(emptyForm(), { parent_id: row.id, company_id: row.company_id })
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleEdit(row) {
      this.loadPersons()
      // 编辑不拉详情接口（列表行字段已够回显）
      this.form = Object.assign(emptyForm(), row)
      // 0 是存库值不是显示值：顶级部门 parent_id=0 / 未设负责人 leader_id=0 都转空
      if (!this.form.parent_id) this.form.parent_id = undefined
      if (!this.form.leader_id) this.form.leader_id = undefined
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
    },
    handleDelete(row) {
      // 有子部门或挂着人员时后端会拒绝，提示由拦截器统一弹
      this.$confirm(`确认删除部门「${row.name}」？`, '删除确认', { type: 'warning' }).then(() => {
        deleteDept(row.id).then(() => {
          this.$message.success('已删除')
          this.loadData()
        })
      }).catch(() => {})
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const { id, ...rest } = this.form
        // 下拉未选（undefined）时归一化：顶级部门/未设负责人存 0
        rest.parent_id = rest.parent_id || 0
        rest.leader_id = rest.leader_id || 0
        const request = this.form.id ? updateDept(id, rest) : createDept(rest)
        request.then(() => {
          this.$message.success(this.form.id ? '已保存修改' : '新增成功')
          this.dialogVisible = false
          this.loadData()
        })
      })
    }
  }
}
</script>
